import { Capacitor } from "@capacitor/core"
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics"

/**
 * Haptic feedback helpers. On native (iOS/Android) these use the system haptic
 * engine via @capacitor/haptics; in the browser they fall back to the
 * Vibration API where available. All helpers are fire-and-forget and swallow
 * errors — feedback must never break the action it accompanies.
 */

const isNative = Capacitor.isNativePlatform()

function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern)
  } catch {
    // Vibration API unavailable (e.g. iOS Safari) — silently skip.
  }
}

/** Light tap — button presses, FAB, toggles. */
export function hapticLight(): void {
  if (isNative) {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {})
  } else {
    vibrate(10)
  }
}

/** Medium tap — gesture thresholds (pull-to-refresh trigger, swipe-open). */
export function hapticMedium(): void {
  if (isNative) {
    Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {})
  } else {
    vibrate(20)
  }
}

/** Success notification — a transaction saved, a goal reached. */
export function hapticSuccess(): void {
  if (isNative) {
    Haptics.notification({ type: NotificationType.Success }).catch(() => {})
  } else {
    vibrate([10, 40, 10])
  }
}

/** Warning notification — budget alerts, destructive confirms. */
export function hapticWarning(): void {
  if (isNative) {
    Haptics.notification({ type: NotificationType.Warning }).catch(() => {})
  } else {
    vibrate([20, 60, 20])
  }
}

/** Error notification — failed saves, rejected input. */
export function hapticError(): void {
  if (isNative) {
    Haptics.notification({ type: NotificationType.Error }).catch(() => {})
  } else {
    vibrate([30, 60, 30, 60, 30])
  }
}
